import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DomainIcon from '@mui/icons-material/Domain';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyIcon from '@mui/icons-material/Key';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {QRCodeSVG} from 'qrcode.react';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';

import {useApplicationContext} from '../../ApplicationContext';
import {AdConversionStatusDto, EUserGroup, MfaStatusDto, TotpSetupDto} from '../../apiClient/Api';
import {AdConversionDialog} from '../../Components/AdConversionDialog';

const formatDateTime = (value: string | null | undefined, locale: string): string => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString(locale);
    } catch {
        return value;
    }
};

const RecoveryCodeList: React.FC<{codes: string[]}> = ({codes}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const theme = useTheme();

    const copyAll = () => {
        navigator.clipboard.writeText(codes.join('\n')).then(() => {
            enqueueSnackbar(t('mfa.recoveryCodes.copied'), {variant: 'success'});
        });
    };

    const download = () => {
        const blob = new Blob([codes.join('\n')], {type: 'text/plain;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'reichert-recovery-codes.txt';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
                    gap: 1,
                    p: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    letterSpacing: '0.1em',
                }}
            >
                {codes.map((code) => (
                    <Box key={code} sx={{textAlign: 'center', py: 0.5}}>
                        {code}
                    </Box>
                ))}
            </Box>
            <Stack direction="row" spacing={1} sx={{mt: 2}}>
                <Button startIcon={<ContentCopyIcon/>} onClick={copyAll} size="small">
                    {t('mfa.recoveryCodes.copyAll')}
                </Button>
                <Button startIcon={<DownloadIcon/>} onClick={download} size="small">
                    {t('mfa.recoveryCodes.download')}
                </Button>
            </Stack>
        </Box>
    );
};

interface SetupFlowProps {
    onComplete: () => void;
    onCancel?: () => void;
    forced: boolean;
}

const SetupFlow: React.FC<SetupFlowProps> = ({onComplete, onCancel, forced}) => {
    const context = useApplicationContext();
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [step, setStep] = useState(0);
    const [setup, setSetup] = useState<TotpSetupDto | null>(null);
    const [code, setCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        context.api.user.userStartTotpSetup()
            .then((res) => {
                if (cancelled) return;
                setSetup(res.data);
            })
            .catch(context.apiError)
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleVerify = async () => {
        if (!setup?.secret || !/^\d{6}$/.test(code)) return;
        setSubmitting(true);
        try {
            const res = await context.api.user.userEnableTotp({code, secret: setup.secret});
            setRecoveryCodes(res.data.recoveryCodes ?? []);
            setStep(2);
        } catch (e) {
            context.apiError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const copySecret = () => {
        if (!setup?.secret) return;
        navigator.clipboard.writeText(setup.secret).then(() => {
            enqueueSnackbar(t('mfa.setup.secretCopied'), {variant: 'success'});
        });
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!setup) {
        return <Alert severity="error">{t('mfa.setup.failed')}</Alert>;
    }

    return (
        <Box>
            <Stepper activeStep={step} alternativeLabel sx={{mb: 4}}>
                <Step>
                    <StepLabel>{t('mfa.setup.steps.scan')}</StepLabel>
                </Step>
                <Step>
                    <StepLabel>{t('mfa.setup.steps.verify')}</StepLabel>
                </Step>
                <Step>
                    <StepLabel>{t('mfa.setup.steps.recovery')}</StepLabel>
                </Step>
            </Stepper>

            {step === 0 && (
                <Stack spacing={3} sx={{alignItems: 'center'}}>
                    <Typography variant="body1" sx={{textAlign: 'center', maxWidth: 480}}>
                        {t('mfa.setup.scanIntro')}
                    </Typography>
                    {setup.otpAuthUri && (
                        <Paper elevation={2} sx={{p: 3, backgroundColor: 'common.white'}}>
                            <QRCodeSVG value={setup.otpAuthUri} size={208} level="M"/>
                        </Paper>
                    )}
                    <Box sx={{width: '100%', maxWidth: 480}}>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            {t('mfa.setup.manualKey')}
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontFamily: 'monospace',
                                fontSize: '1rem',
                                letterSpacing: '0.1em',
                                wordBreak: 'break-all',
                            }}
                        >
                            <Box sx={{flex: 1}}>{setup.secret}</Box>
                            <Tooltip title={t('mfa.setup.copySecret')}>
                                <IconButton size="small" onClick={copySecret}>
                                    <ContentCopyIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </Box>
                    <Stack direction="row" spacing={2} sx={{width: '100%', justifyContent: 'flex-end'}}>
                        {onCancel && !forced && (
                            <Button onClick={onCancel} color="inherit">
                                {t('mfa.common.cancel')}
                            </Button>
                        )}
                        <Button variant="contained" onClick={() => setStep(1)}>
                            {t('mfa.setup.next')}
                        </Button>
                    </Stack>
                </Stack>
            )}

            {step === 1 && (
                <Stack spacing={3} sx={{alignItems: 'center'}}>
                    <Typography variant="body1" sx={{textAlign: 'center', maxWidth: 480}}>
                        {t('mfa.setup.verifyIntro')}
                    </Typography>
                    <TextField
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        label={t('mfa.setup.codeLabel')}
                        placeholder="••••••"
                        slotProps={{
                            htmlInput: {
                                maxLength: 6,
                                inputMode: 'numeric',
                                style: {
                                    textAlign: 'center',
                                    letterSpacing: '0.5em',
                                    fontSize: '1.6rem',
                                    fontFamily: 'monospace',
                                },
                            },
                        }}
                        sx={{width: 240}}
                    />
                    <Stack direction="row" spacing={2} sx={{width: '100%', justifyContent: 'space-between'}}>
                        <Button onClick={() => setStep(0)} color="inherit">
                            {t('mfa.common.back')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleVerify}
                            disabled={!/^\d{6}$/.test(code) || submitting}
                        >
                            {submitting ? t('mfa.setup.verifying') : t('mfa.setup.verify')}
                        </Button>
                    </Stack>
                </Stack>
            )}

            {step === 2 && (
                <Stack spacing={3}>
                    <Alert severity="success" icon={<VerifiedUserIcon/>}>
                        <AlertTitle>{t('mfa.setup.successTitle')}</AlertTitle>
                        {t('mfa.setup.successText')}
                    </Alert>
                    <Alert severity="warning" icon={<WarningAmberIcon/>}>
                        <AlertTitle>{t('mfa.recoveryCodes.title')}</AlertTitle>
                        {t('mfa.recoveryCodes.intro')}
                    </Alert>
                    <RecoveryCodeList codes={recoveryCodes}/>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button variant="contained" onClick={onComplete}>
                            {t('mfa.recoveryCodes.confirm')}
                        </Button>
                    </Box>
                </Stack>
            )}
        </Box>
    );
};

interface DisableDialogProps {
    open: boolean;
    onClose: () => void;
    onDisabled: () => void;
}

const DisableDialog: React.FC<DisableDialogProps> = ({open, onClose, onDisabled}) => {
    const context = useApplicationContext();
    const {t} = useTranslation();
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setCode('');
            setSubmitting(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        if (!code.trim()) return;
        setSubmitting(true);
        try {
            await context.api.user.userDisableTotp({code: code.trim()});
            onDisabled();
        } catch (e) {
            context.apiError(e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('mfa.disable.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>{t('mfa.disable.text')}</DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    label={t('mfa.disable.codeLabel')}
                    placeholder="••••••"
                    slotProps={{
                        htmlInput: {
                            style: {
                                textAlign: 'center',
                                letterSpacing: '0.4em',
                                fontSize: '1.4rem',
                                fontFamily: 'monospace',
                            },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    {t('mfa.common.cancel')}
                </Button>
                <Button onClick={handleConfirm} color="error" variant="contained" disabled={!code.trim() || submitting}>
                    {submitting ? t('mfa.disable.submitting') : t('mfa.disable.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface RegenerateDialogProps {
    open: boolean;
    onClose: () => void;
    onRegenerated: () => void;
}

const RegenerateDialog: React.FC<RegenerateDialogProps> = ({open, onClose, onRegenerated}) => {
    const context = useApplicationContext();
    const {t} = useTranslation();
    const [codes, setCodes] = useState<string[] | null>(null);
    const [loading, setLoading] = useState(false);

    const handleStart = async () => {
        setLoading(true);
        try {
            const res = await context.api.user.userRegenerateRecoveryCodes();
            setCodes(res.data.recoveryCodes ?? []);
            onRegenerated();
        } catch (e) {
            context.apiError(e);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCodes(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('mfa.regenerate.title')}</DialogTitle>
            <DialogContent>
                {!codes ? (
                    <DialogContentText>{t('mfa.regenerate.text')}</DialogContentText>
                ) : (
                    <Stack spacing={2}>
                        <Alert severity="warning">{t('mfa.recoveryCodes.intro')}</Alert>
                        <RecoveryCodeList codes={codes}/>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {!codes ? (
                    <>
                        <Button onClick={handleClose} color="inherit">
                            {t('mfa.common.cancel')}
                        </Button>
                        <Button onClick={handleStart} variant="contained" disabled={loading}>
                            {loading ? t('mfa.regenerate.submitting') : t('mfa.regenerate.confirm')}
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleClose} variant="contained">
                        {t('mfa.recoveryCodes.confirm')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

interface SecuritySettingsProps {
    forced?: boolean;
}

export const SecuritySettings: React.FC<SecuritySettingsProps> = ({forced = false}) => {
    const context = useApplicationContext();
    const {t, i18n} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [status, setStatus] = useState<MfaStatusDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [setupOpen, setSetupOpen] = useState(false);
    const [disableOpen, setDisableOpen] = useState(false);
    const [regenerateOpen, setRegenerateOpen] = useState(false);
    const [labelUser, setLabelUser] = useState<string | null | undefined>(undefined);
    const [adStatus, setAdStatus] = useState<AdConversionStatusDto | null>(null);
    const [adDialogOpen, setAdDialogOpen] = useState(false);

    useEffect(() => {
        const id = context.userData?.id;
        if (forced || !id) return;
        context.api.user
            .userGetUser(id)
            .then((res) => setLabelUser(res.data.labelUser ?? null))
            .catch(() => setLabelUser(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.userData?.id, forced]);

    const loadStatus = useCallback(() => {
        setLoading(true);
        context.api.user.userGetMfaStatus()
            .then((res) => setStatus(res.data))
            .catch(context.apiError)
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.api]);

    const loadAdStatus = useCallback(() => {
        // Nur für interne Konten relevant; Kunden bekommen die Karte gar nicht erst.
        const group = context.userData?.userGroup;
        if (forced || group === undefined || group > EUserGroup.OFFICE) return;
        context.api.user.userGetAdConversionStatus()
            .then((res) => setAdStatus(res.data))
            .catch(() => setAdStatus(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.api, forced]);

    useEffect(() => {
        loadAdStatus();
    }, [loadAdStatus]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (forced && status && !status.totpEnabled) {
            setSetupOpen(true);
        }
    }, [forced, status]);

    const roleRequiresMfa = useMemo(() => {
        if (status?.mfaRequiredByRole !== undefined) return status.mfaRequiredByRole;
        const group = context.userData?.userGroup;
        return group !== undefined && group <= EUserGroup.DEVELOPMENT;
    }, [status, context.userData]);

    const handleSetupComplete = () => {
        setSetupOpen(false);
        if (forced) {
            context.completeMfaSetup();
        }
        loadStatus();
        enqueueSnackbar(t('mfa.setup.success'), {variant: 'success'});
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{py: 6, display: 'flex', justifyContent: 'center'}}>
                <CircularProgress/>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{py: {xs: 3, sm: 5}}}>
            <Stack spacing={3}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <ShieldOutlinedIcon color="primary" sx={{fontSize: 40}}/>
                    <Box>
                        <Typography variant="h4" component="h1">
                            {t('mfa.page.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('mfa.page.subtitle')}
                        </Typography>
                    </Box>
                </Box>

                {forced && (
                    <Alert severity="warning" icon={<WarningAmberIcon/>}>
                        <AlertTitle>{t('mfa.forced.title')}</AlertTitle>
                        {t('mfa.forced.text')}
                    </Alert>
                )}

                {!forced && labelUser !== undefined && (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('security.labelUser.title')}
                            </Typography>
                            {labelUser ? (
                                <Typography variant="body1" sx={{fontFamily: 'monospace'}}>
                                    {labelUser}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t('security.labelUser.none')}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                {t('security.labelUser.hint')}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                <Card variant="outlined">
                    <CardContent>
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            spacing={2}
                            sx={{
                                alignItems: {xs: 'flex-start', sm: 'center'},
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
                                <KeyIcon color={status?.totpEnabled ? 'success' : 'disabled'} sx={{fontSize: 32}}/>
                                <Box>
                                    <Typography variant="h6">{t('mfa.totp.title')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('mfa.totp.description')}
                                    </Typography>
                                </Box>
                            </Stack>
                            {status?.totpEnabled ? (
                                <Chip
                                    icon={<VerifiedUserIcon/>}
                                    color="success"
                                    label={t('mfa.totp.active')}
                                />
                            ) : (
                                <Chip color="default" label={t('mfa.totp.inactive')}/>
                            )}
                        </Stack>

                        {status?.totpEnabled && (
                            <Box sx={{mt: 3}}>
                                <Divider sx={{mb: 2}}/>
                                <Stack spacing={1.5}>
                                    {status.totpEnabledAt && (
                                        <Typography variant="body2">
                                            <strong>{t('mfa.totp.activatedAt')}:</strong>{' '}
                                            {formatDateTime(status.totpEnabledAt, i18n.language)}
                                        </Typography>
                                    )}
                                    <Typography variant="body2">
                                        <strong>{t('mfa.totp.recoveryRemaining')}:</strong>{' '}
                                        {status.remainingRecoveryCodes ?? 0}
                                        {(status.remainingRecoveryCodes ?? 0) <= 2 && (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label={t('mfa.totp.recoveryLow')}
                                                sx={{ml: 1}}
                                            />
                                        )}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mt: 3}}>
                            {!status?.totpEnabled && (
                                <Button
                                    variant="contained"
                                    startIcon={<ShieldOutlinedIcon/>}
                                    onClick={() => setSetupOpen(true)}
                                >
                                    {t('mfa.totp.activate')}
                                </Button>
                            )}
                            {status?.totpEnabled && (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon/>}
                                        onClick={() => setRegenerateOpen(true)}
                                    >
                                        {t('mfa.totp.regenerateCodes')}
                                    </Button>
                                    <Tooltip title={roleRequiresMfa ? t('mfa.totp.disableBlocked') : ''}>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => setDisableOpen(true)}
                                                disabled={roleRequiresMfa}
                                            >
                                                {t('mfa.totp.disable')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                {adStatus && (
                    <Card variant="outlined">
                        <CardContent>
                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                spacing={2}
                                sx={{
                                    alignItems: {xs: 'flex-start', sm: 'center'},
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
                                    <DomainIcon
                                        color={adStatus.alreadyConnected ? 'success' : 'disabled'}
                                        sx={{fontSize: 32}}/>
                                    <Box>
                                        <Typography variant="h6">{t('adLogin.title')}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('adLogin.description')}
                                        </Typography>
                                    </Box>
                                </Stack>
                                {adStatus.alreadyConnected ? (
                                    <Chip
                                        icon={<VerifiedUserIcon/>}
                                        color="success"
                                        label={adStatus.userPrincipalName ?? t('adLogin.active')}
                                    />
                                ) : (
                                    <Chip color="default" label={t('adLogin.inactive')}/>
                                )}
                            </Stack>

                            {/* Die Begründung kommt vom Server und wird unverändert angezeigt. */}
                            <Typography variant="body2" sx={{mt: 2}}>
                                {adStatus.message}
                            </Typography>

                            {adStatus.eligible && (
                                <Stack direction="row" spacing={1.5} sx={{mt: 2}}>
                                    <Button
                                        variant="contained"
                                        startIcon={<DomainIcon/>}
                                        onClick={() => setAdDialogOpen(true)}
                                    >
                                        {t('adLogin.convert')}
                                    </Button>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                )}

                {roleRequiresMfa && (
                    <Alert severity="info">
                        {t('mfa.roleRequirement')}
                    </Alert>
                )}
            </Stack>

            <AdConversionDialog
                open={adDialogOpen}
                account={adStatus?.account}
                onClose={(reason) => {
                    setAdDialogOpen(false);
                    if (reason === 'converted') loadAdStatus();
                }}
            />

            <Dialog
                open={setupOpen}
                onClose={forced ? undefined : () => setSetupOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('mfa.setup.title')}</DialogTitle>
                <DialogContent>
                    <SetupFlow
                        forced={forced}
                        onCancel={() => setSetupOpen(false)}
                        onComplete={handleSetupComplete}
                    />
                </DialogContent>
            </Dialog>

            <DisableDialog
                open={disableOpen}
                onClose={() => setDisableOpen(false)}
                onDisabled={() => {
                    setDisableOpen(false);
                    enqueueSnackbar(t('mfa.disable.success'), {variant: 'success'});
                    loadStatus();
                }}
            />

            <RegenerateDialog
                open={regenerateOpen}
                onClose={() => setRegenerateOpen(false)}
                onRegenerated={loadStatus}
            />
        </Container>
    );
};

export default SecuritySettings;
